import React, { useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Typography,
} from '@mui/material';

import { Checkbox, Input, Select } from '~/components/Common/Forms/fields';

import { useCppPartners, useCreateCppPartner } from '~/hooks/apis/costPerPoint';

import {
  CREATE_PARTNER_DEFAULT_VALUES,
  type CreatePartnerFormValues,
  FLAG_FIELDS,
  LEGAL_NAME_MAX_LENGTH,
  LIMIT_FIELDS,
  PARTNER_TYPE_LABELS,
  type PartnerTypeOption,
  buildPlaceholderApiKeyId,
  pairedLimitField,
  toCreatePartnerBody,
  validateGstPercentage,
  validateLimit,
  validatePartnerName,
  validateWebhookUrl,
} from './createPartnerForm';

interface Props {
  open: boolean;
  onClose: () => void;
}

const TWO_COLUMNS = { xs: '1fr', sm: 'repeat(2, 1fr)' };

/** Overline section title, with an optional caption under it. */
function SectionHeading({ title, caption }: { title: string; caption?: string }) {
  return (
    <Box mb={1.5}>
      <Typography variant="overline" color="text.secondary" display="block">
        {title}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" display="block">
          {caption}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Creates a partner.
 *
 * The admin fills in everything the create endpoint accepts except `apiKeyId`, which is set to a
 * random placeholder (see {@link buildPlaceholderApiKeyId}) because the API Gateway key is
 * provisioned separately.
 */
export default function CreatePartnerDialog({ open, onClose }: Props) {
  const createMutation = useCreateCppPartner();
  // Same cached query as the directory behind the dialog, so this adds no request.
  const { data: partnersData } = useCppPartners();
  const existingNames = useMemo(() => (partnersData?.result ?? []).map((partner) => partner.name), [partnersData]);

  const { control, handleSubmit, reset, getValues, watch, trigger, getFieldState } =
    useForm<CreatePartnerFormValues>({
      defaultValues: CREATE_PARTNER_DEFAULT_VALUES,
      mode: 'onBlur',
    });

  // Start every opening from a blank form, so an abandoned draft never carries into the next partner.
  useEffect(() => {
    if (open) {
      reset(CREATE_PARTNER_DEFAULT_VALUES);
    }
  }, [open, reset]);

  // A min/max limit is valid only relative to its pair, so an edit to one side re-checks the other.
  // Only once that side has been touched or already shows an error (e.g. after a submit), so an
  // untouched field does not light up early and a stale error still clears.
  useEffect(() => {
    const subscription = watch((_values, { name }) => {
      const paired = name && pairedLimitField(name);
      if (!paired) {
        return;
      }
      const { isTouched, invalid } = getFieldState(paired);
      if (isTouched || invalid) {
        trigger(paired);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger, getFieldState]);

  const onSubmit = async (values: CreatePartnerFormValues) => {
    try {
      await createMutation.mutateAsync(toCreatePartnerBody(values, buildPlaceholderApiKeyId()));
      onClose();
    } catch {
      // The hook raises the error toast; swallowing the rejection keeps the dialog open on the
      // entered values and avoids an unhandled rejection.
    }
  };

  const isSaving = createMutation.isPending;

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create partner</DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          The partner is created active with a placeholder API Gateway key ID. Requests to the internal API work
          straight away. Requests through the API Gateway are rejected until an engineer replaces the placeholder with
          the partner&apos;s real key ID.
        </Alert>

        <SectionHeading title="Partner" />
        <Box display="grid" gridTemplateColumns={TWO_COLUMNS} gap={2}>
          <Input
            control={control}
            name="name"
            muiLabel="Name"
            required
            fullWidth
            helperText="Partners must send this exact name on every API call"
            rules={{ validate: (value: string) => validatePartnerName(value, existingNames) }}
          />
          <Select control={control} name="partnerType" muiLabel="Partner type" rules={{ required: 'Choose a type' }}>
            {(Object.keys(PARTNER_TYPE_LABELS) as Array<PartnerTypeOption>).map((type) => (
              <MenuItem key={type} value={type}>
                {PARTNER_TYPE_LABELS[type]}
              </MenuItem>
            ))}
          </Select>
          <Box gridColumn="1 / -1">
            <Input
              control={control}
              name="webhookUrl"
              muiLabel="Webhook URL"
              type="url"
              fullWidth
              rules={{ validate: validateWebhookUrl }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <SectionHeading title="Billing details" caption="Used on the partner invoice." />
        <Box display="grid" gridTemplateColumns={TWO_COLUMNS} gap={2}>
          <Input
            control={control}
            name="legalName"
            muiLabel="Legal entity name"
            fullWidth
            helperText="Falls back to the name on the invoice when empty"
            rules={{
              maxLength: {
                value: LEGAL_NAME_MAX_LENGTH,
                message: `Must be at most ${LEGAL_NAME_MAX_LENGTH} characters`,
              },
            }}
          />
          <Input
            control={control}
            name="gstPercentage"
            muiLabel="GST rate"
            type="number"
            fullWidth
            helperText="Percent, e.g. 10. Leave blank for the service default"
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            rules={{ validate: validateGstPercentage }}
          />
          <Box gridColumn="1 / -1">
            <Input control={control} name="address" muiLabel="Billing address" rows={3} fullWidth />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <SectionHeading title="Limits" caption="Leave blank to use the service default." />
        <Box display="grid" gridTemplateColumns={TWO_COLUMNS} gap={2}>
          {LIMIT_FIELDS.map((config) => (
            <Input
              key={config.field}
              control={control}
              name={config.field}
              muiLabel={config.label}
              type="number"
              fullWidth
              inputProps={{ min: config.min, step: 1 }}
              rules={{ validate: () => validateLimit(config, getValues()) }}
            />
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        <SectionHeading title="Permissions" />
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}>
          {FLAG_FIELDS.map(({ field, label }) => (
            <Checkbox key={field} control={control} name={field} label={label} />
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSaving}>
          {isSaving ? 'Creating…' : 'Create partner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
